import { forwardRef, useCallback, useMemo } from "react";
import { Button, Icon, Spinner, Popover } from "@/components/atoms";
import { getOldBookingUrl } from "@/helpers/bookingHelpers";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useCommenceJobMutation, useConfirmDriverMutation, useMarkDriverOnWayMutation } from "@/redux/apis/booking/endpoints/jobStatus";
import { useAppDispatch } from "@/redux/hooks";
import { setAction } from "@/redux/slices/booking/bookingSlice";
import { BookingAction, BookingDetails } from "@/redux/slices/booking/types";
import { addToast } from "@/utils";
import { BookingNavButton } from "../../../common/BookingNavButton/BookingNavButton";
import { useIsOperatorOwnJob } from "../../../hooks/useIsOperatorOwnJob";
import { addBookingNotification } from "../../../utils";

interface BookingActionsBarProps {
  booking: BookingDetails;
}

const SplitButton = forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button> & {
    onPrimaryClick?: () => void;
  }
>((props, ref) => {
  const { onClick, onPrimaryClick, ...buttonProps } = props;
  return <Button ref={ref} {...buttonProps} onClick={onPrimaryClick} onDropdownClick={onClick} />;
});

SplitButton.displayName = "SplitButton";

export const BookingActionsBar = ({ booking }: BookingActionsBarProps) => {
  return (
    <div className="flex flex-wrap items-center gap-2.5 px-5 py-2.5">
      <DriverActionsButton booking={booking} />
      <CloseJobPopover booking={booking} />
      <ViewClosingExtrasNotesButton booking={booking} />
      <ReopenJobButton booking={booking} />
      <ActionsPopover booking={booking} />
      <ViewBookingSheetButton booking={booking} />
    </div>
  );
};

const DriverActionsButton = ({ booking }: { booking: BookingDetails }) => {
  const isOwnJob = useIsOperatorOwnJob();
  const { id: bookingId, status, metadata } = booking;
  const [confirmDriver, { isLoading: isConfirmingDriver }] = useConfirmDriverMutation();
  const [markDriverOnWay, { isLoading: isMarkingDriverOnWay }] = useMarkDriverOnWayMutation();
  const [startTrip, { isLoading: isCommencingJob }] = useCommenceJobMutation();

  const handleConfirmDriver = useCallback(() => {
    if (!booking.driver) throw Error("Cannot confirm driver without driver data");
    confirmDriver({ bookingId, driverId: booking.driver.id })
      .unwrap()
      .then(() => addBookingNotification(`Job ${bookingId} updated`, "Driver confirmed", "driver_allocated_not_confirmed"))
      .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
  }, [booking.driver, bookingId, confirmDriver]);

  const handleDriverOnTheWay = useCallback(() => {
    if (!booking.driver) throw Error("Cannot confirm driver is on the way to pickup without driver data");
    markDriverOnWay({ bookingId })
      .unwrap()
      .then(() => addBookingNotification(`Job ${bookingId} updated`, "Driver is on the way to pickup", "driver_allocated_confirmed"))
      .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
  }, [booking.driver, bookingId, markDriverOnWay]);

  const handleDriverStartedTrip = useCallback(() => {
    if (!booking.driver) throw Error("Cannot confirm driver has started the trip without driver data");
    startTrip({ bookingId })
      .unwrap()
      .then(() => addBookingNotification(`Job ${bookingId} updated`, "Driver has started the trip", "on_way_to_job"))
      .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
  }, [booking.driver, bookingId, startTrip]);

  const [handleClick, isLoading, buttonContent] = useMemo(() => {
    switch (status) {
      case "driver_allocated_not_confirmed":
        return [handleConfirmDriver, isConfirmingDriver, "Confirm Driver"];
      case "driver_allocated_confirmed":
        return [handleDriverOnTheWay, isMarkingDriverOnWay, "Confirm Driver on the Way to Pickup"];
      case "on_way_to_job":
        return [handleDriverStartedTrip, isCommencingJob, "Confirm Driver has Started Trip"];
      default:
        return [() => {}, false, ""];
    }
  }, [
    handleConfirmDriver,
    handleDriverOnTheWay,
    handleDriverStartedTrip,
    isCommencingJob,
    isConfirmingDriver,
    isMarkingDriverOnWay,
    status,
  ]);

  if (!buttonContent || (metadata.isOffloaded && !isOwnJob)) return null;

  return (
    <Button
      onClick={handleClick}
      disabled={isLoading}
      size="sm"
      variant="info"
      className="animate-ripple items-center outline-info [&>.typography]:flex [&>.typography]:items-center"
    >
      {isLoading ? <Spinner className="mr-2" variant="light" /> : <Icon name="TickCircle" size="sm" className="mr-2" />}
      {buttonContent}
    </Button>
  );
};

const CloseJobPopover = ({ booking }: { booking: BookingDetails }) => {
  const dispatch = useAppDispatch();
  const isOwnJob = useIsOperatorOwnJob();
  const { status, metadata } = booking;

  const setBookingAction = (action: BookingAction) => dispatch(setAction(action));

  const getFirstAction = () => {
    switch (status) {
      case "commenced":
      case "complete_awaiting_review":
        return () => setBookingAction("close-job-completed");
      default:
        return () => setBookingAction("close-job-no-cost");
    }
  };

  const hasCloseNavButtons = () => {
    switch (status) {
      case "closed":
      case "cancelled":
        return false;
      case "commenced":
      case "complete_awaiting_review":
        return true;
      default:
        return true;
    }
  };

  const renderCloseNavButtons = () => {
    switch (status) {
      case "commenced":
      case "complete_awaiting_review":
        return (
          <>
            <BookingNavButton onClick={() => setBookingAction("close-job-completed")}>Close Job</BookingNavButton>
            <BookingNavButton onClick={() => setBookingAction("close-job-no-cost")}>Close Job at No Cost</BookingNavButton>
            <BookingNavButton onClick={() => setBookingAction("allocate-new-driver")}>Driver No Show</BookingNavButton>
          </>
        );
      default:
        return (
          <>
            <BookingNavButton onClick={() => setBookingAction("close-job-no-cost")}>Close at No Cost</BookingNavButton>
            {metadata.isCancelAllowed && (
              <BookingNavButton onClick={() => setBookingAction("close-job-customer-cancel")}>Cancelled By Customer</BookingNavButton>
            )}
            <BookingNavButton onClick={() => setBookingAction("close-job-no-driver")}>No Driver Available</BookingNavButton>
          </>
        );
    }
  };

  if (metadata.isOffloaded && !isOwnJob) return null;

  if (!hasCloseNavButtons()) return null;

  const firstAction = getFirstAction();

  return (
    <Popover placement="bottom-start">
      <SplitButton size="sm" isDropdown onPrimaryClick={firstAction || undefined}>
        Close Job
      </SplitButton>
      <div className="flex w-[270px] flex-col rounded-lg bg-white pb-3 pt-3 shadow-dropdown">{renderCloseNavButtons()}</div>
    </Popover>
  );
};

const ViewClosingExtrasNotesButton = ({ booking }: { booking: BookingDetails }) => {
  const dispatch = useAppDispatch();
  const isOwnJob = useIsOperatorOwnJob();
  const { metadata, status, closeMeta } = booking;

  const setBookingAction = (action: BookingAction) => dispatch(setAction(action));

  if (status !== "closed" && status !== "cancelled") return null;

  if (metadata.isOffloaded && !isOwnJob) return null;

  if (!closeMeta) return null;

  return (
    <Button variant="primary" size="sm" onClick={() => setBookingAction("view-closing-extras-notes")}>
      View Closing Extras and Notes
    </Button>
  );
};

const ReopenJobButton = ({ booking }: { booking: BookingDetails }) => {
  const dispatch = useAppDispatch();
  const isOwnJob = useIsOperatorOwnJob();
  const { metadata, status } = booking;

  const setBookingAction = (action: BookingAction) => dispatch(setAction(action));

  if (status !== "closed" && status !== "cancelled") return null;

  if (metadata.isOffloaded && !isOwnJob) return null;

  if (!metadata.isReopenAllowed) return null;

  return (
    <Button variant="secondary" size="sm" onClick={() => setBookingAction("reopen-job")}>
      Reopen Job
    </Button>
  );
};

const ActionsPopover = ({ booking }: { booking: BookingDetails }) => {
  const dispatch = useAppDispatch();
  const isOwnJob = useIsOperatorOwnJob();
  const { metadata } = booking;

  const setBookingAction = (action: BookingAction) => dispatch(setAction(action));

  const isOffloadEnabled =
    (!metadata.isOffloaded || isOwnJob) && !["commenced", "complete_awaiting_review", "closed", "cancelled"].includes(booking.status);

  const showForwardConfirmation = !metadata.isOffloaded || isOwnJob;

  if (!showForwardConfirmation && !isOffloadEnabled) {
    return null;
  }

  return (
    <Popover placement="bottom-start">
      <Button variant="primary" size="sm" endIcon="ArrowDown2" iconVariant="Bold">
        Actions
      </Button>
      <div className="flex w-[270px] flex-col rounded-lg bg-white pb-3 pt-3 shadow-dropdown">
        <nav className="flex flex-col">
          <div className="flex list-none flex-col">
            {showForwardConfirmation && (
              <BookingNavButton onClick={() => setBookingAction("forward-confirmation")}>Forward Booking Confirmation</BookingNavButton>
            )}
            {isOffloadEnabled && (
              <BookingNavButton onClick={() => setBookingAction("offload-to-network")}>Offload Job to Your Networks</BookingNavButton>
            )}
          </div>
        </nav>
      </div>
    </Popover>
  );
};

const ViewBookingSheetButton = ({ booking }: { booking: BookingDetails }) => {
  const { id: bookingId, account } = booking;

  const handleOpenJob = () => {
    if (!account) return;
    window.open(getOldBookingUrl(account?.id, bookingId, "").replace("/admin/", ""), "_blank");
  };

  if (!account) return null;

  return (
    <Button onClick={handleOpenJob} size="sm" variant="secondary" startIcon="Eye">
      View Booking Sheet
    </Button>
  );
};
